// Measured change between two dates of the same site, from two georeferenced
// point clouds (LAS). Both clouds go onto one grid of average heights, the
// later one is coloured by how much each cell rose or fell, and the cells add
// up to fill, cut and area changed. Pure: no three.js, no DOM.

export const MIN_CHANGE = 0.1; // metres; smaller differences count as no change
export const FULL_SCALE = 2; // metres of change that reach the full colour
export const MAX_CELLS = 1000000;

const RAISED = [0.824, 0.329, 0.169]; // Bricks brick #D2542B
const LOWERED = [0.431, 0.561, 0.702]; // slate #6E8FB3
const NEUTRAL = 0.46;
const NO_DATA = 0.24;

export const LEGEND = [
  { key: 'raised', label: 'Higher', color: '#D2542B' },
  { key: 'lowered', label: 'Lower', color: '#6E8FB3' },
  { key: 'same', label: 'No change', color: '#757575' },
];

export class ChangeError extends Error {}

// Why a pair can't be measured, or null when it can.
export function changeBlocker(a, b) {
  const fa = a && a.format;
  const fb = b && b.format;
  if (fa !== 'las' || fb !== 'las') return 'Changes need two point clouds saved as .las.';
  return null;
}

function overlap(a, b) {
  const w = Math.min(a.maxX, b.maxX) - Math.max(a.minX, b.minX);
  const h = Math.min(a.maxY, b.maxY) - Math.max(a.minY, b.minY);
  if (w <= 0 || h <= 0) return 0;
  const area = (x) => Math.max(1e-9, (x.maxX - x.minX) * (x.maxY - x.minY));
  return (w * h) / Math.min(area(a), area(b));
}

// Coordinates in degrees (lat/long) can't be measured in metres.
function inDegrees(bd) {
  return Math.abs(bd.maxX) <= 180 && Math.abs(bd.maxY) <= 90 && bd.maxX - bd.minX < 0.5 && bd.maxY - bd.minY < 0.5;
}

export function checkAligned(a, b) {
  if (!a || !b || !a.bounds || !b.bounds) throw new ChangeError("These point clouds don't say where they are.");
  if (inDegrees(a.bounds) || inDegrees(b.bounds)) throw new ChangeError('These point clouds are in degrees. Changes need a metre grid, like UTM.');
  if (overlap(a.bounds, b.bounds) < 0.25) throw new ChangeError("These two captures don't line up on the same survey grid.");
}

export function cellSize(bounds, min = 0.5) {
  const area = Math.max(1, (bounds.maxX - bounds.minX) * (bounds.maxY - bounds.minY));
  return Math.max(min, Math.sqrt(area / MAX_CELLS));
}

// cloud: parseLAS result. shift: metres to add so it sits in the shared frame.
function heights(cloud, shift, grid) {
  const { x0, y0, cols, rows, cell } = grid;
  const sum = new Float64Array(cols * rows);
  const cnt = new Uint32Array(cols * rows);
  const p = cloud.positions;
  for (let i = 0; i < cloud.count; i += 1) {
    const c = Math.floor((p[i * 3] + shift[0] - x0) / cell);
    const r = Math.floor((p[i * 3 + 1] + shift[1] - y0) / cell);
    if (c < 0 || r < 0 || c >= cols || r >= rows) continue;
    const k = r * cols + c;
    sum[k] += p[i * 3 + 2] + shift[2];
    cnt[k] += 1;
  }
  const out = new Float32Array(cols * rows).fill(NaN);
  for (let k = 0; k < out.length; k += 1) if (cnt[k]) out[k] = sum[k] / cnt[k];
  return out;
}

function mix(base, to, t) {
  return [base + (to[0] - base) * t, base + (to[1] - base) * t, base + (to[2] - base) * t];
}

// Returns the later cloud in the earlier cloud's frame, coloured by change, and the totals.
export function measureChange(early, late, opts = {}) {
  checkAligned(early, late);
  const minChange = opts.minChange || MIN_CHANGE;
  const shift = [0, 1, 2].map((j) => late.origin[j] - early.origin[j]);
  const lb = late.bounds;
  const eb = early.bounds;
  const bounds = {
    minX: Math.max(eb.minX, lb.minX) - early.origin[0], maxX: Math.min(eb.maxX, lb.maxX) - early.origin[0],
    minY: Math.max(eb.minY, lb.minY) - early.origin[1], maxY: Math.min(eb.maxY, lb.maxY) - early.origin[1],
  };
  const cell = opts.cell || cellSize(bounds);
  const cols = Math.max(1, Math.ceil((bounds.maxX - bounds.minX) / cell));
  const rows = Math.max(1, Math.ceil((bounds.maxY - bounds.minY) / cell));
  const grid = { x0: bounds.minX, y0: bounds.minY, cols, rows, cell };
  const a = heights(early, [0, 0, 0], grid);
  const b = heights(late, shift, grid);
  const dz = new Float32Array(cols * rows).fill(NaN);
  const area = cell * cell;
  let fill = 0;
  let cut = 0;
  let changed = 0;
  let compared = 0;
  for (let k = 0; k < dz.length; k += 1) {
    if (Number.isNaN(a[k]) || Number.isNaN(b[k])) continue;
    const d = b[k] - a[k];
    dz[k] = d;
    compared += 1;
    if (d > minChange) { fill += d * area; changed += 1; } else if (d < -minChange) { cut += -d * area; changed += 1; }
  }
  if (!compared) throw new ChangeError("These two captures don't overlap.");

  const n = late.count;
  const positions = new Float32Array(n * 3);
  const colors = new Float32Array(n * 3);
  const p = late.positions;
  for (let i = 0; i < n; i += 1) {
    const x = p[i * 3] + shift[0];
    const y = p[i * 3 + 1] + shift[1];
    positions[i * 3] = x; positions[i * 3 + 1] = y; positions[i * 3 + 2] = p[i * 3 + 2] + shift[2];
    const c = Math.floor((x - grid.x0) / cell);
    const r = Math.floor((y - grid.y0) / cell);
    const d = c >= 0 && r >= 0 && c < cols && r < rows ? dz[r * cols + c] : NaN;
    let rgb;
    if (Number.isNaN(d)) rgb = [NO_DATA, NO_DATA, NO_DATA];
    else if (Math.abs(d) <= minChange) rgb = [NEUTRAL, NEUTRAL, NEUTRAL];
    else rgb = mix(NEUTRAL, d > 0 ? RAISED : LOWERED, Math.min(1, 0.35 + (0.65 * Math.abs(d)) / FULL_SCALE));
    colors[i * 3] = rgb[0]; colors[i * 3 + 1] = rgb[1]; colors[i * 3 + 2] = rgb[2];
  }
  return {
    positions, colors, count: n,
    summary: { fill, cut, changedArea: changed * area, comparedArea: compared * area, cell, minChange },
  };
}

const n0 = new Intl.NumberFormat('en-CA', { maximumFractionDigits: 0 });
const round = (v) => (v >= 100 ? Math.round(v / 10) * 10 : Math.round(v));

export function volumeLabel(m3) {
  return `${n0.format(round(m3 || 0))} m³`;
}
export function areaLabel(m2) {
  return `${n0.format(round(m2 || 0))} m²`;
}

// At most three plain stats.
export function changeStats(summary) {
  if (!summary) return [];
  return [
    { key: 'fill', label: 'Fill', value: volumeLabel(summary.fill) },
    { key: 'cut', label: 'Cut', value: volumeLabel(summary.cut) },
    { key: 'area', label: 'Area changed', value: areaLabel(summary.changedArea) },
  ];
}

export function methodLine(summary) {
  const cell = summary && summary.cell ? summary.cell : 0.5;
  const cm = Math.round(((summary && summary.minChange) || MIN_CHANGE) * 100);
  const c = cell < 1 ? `${Math.round(cell * 100) / 100} m` : `${Math.round(cell * 10) / 10} m`;
  return `Measured from the two point clouds on a ${c} grid. Changes under ${cm} cm are left out. An estimate for tracking progress, not a survey.`;
}
